import { useVirtualizer, type VirtualItem } from '@tanstack/react-virtual'
import { useRef, useCallback } from 'react'

interface UseVirtualListOptions<T> {
  items: T[]
  estimateSize: number // Estimated row height in px
  overscan?: number // Extra items to render outside viewport (default: 10)
  getItemKey?: (index: number, item: T) => string | number
}

interface UseVirtualListResult {
  containerRef: React.RefObject<HTMLDivElement | null>
  virtualItems: VirtualItem[]
  totalSize: number
  scrollToIndex: (index: number) => void
  measureElement: (node: Element | null) => void
}

export function useVirtualList<T>({
  items,
  estimateSize,
  overscan = 10,
  getItemKey,
}: UseVirtualListOptions<T>): UseVirtualListResult {
  const containerRef = useRef<HTMLDivElement>(null)

  // Deliberately no `measureElement` — both consumers rely on TanStack Virtual's built-in dynamic
  // measurement via the ref it returns. A custom one type-checks and silently breaks row heights,
  // and the unit suite mocks this hook, so nothing would catch it.
  // eslint-disable-next-line react-hooks/incompatible-library -- TanStack Virtual API is safe for our use case
  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => containerRef.current,
    estimateSize: () => estimateSize,
    overscan,
    getItemKey: getItemKey
      ? (index) => getItemKey(index, items[index])
      : (index) => index,
  })

  const scrollToIndex = useCallback(
    (index: number) => {
      virtualizer.scrollToIndex(index, { align: 'start' })
    },
    [virtualizer],
  )

  return {
    containerRef,
    virtualItems: virtualizer.getVirtualItems(),
    totalSize: virtualizer.getTotalSize(),
    scrollToIndex,
    measureElement: virtualizer.measureElement,
  }
}
